import { useCallback, useContext, useMemo } from 'react'
import { ANYWHERE_SEARCH_ITEM } from 'constants/search'
import { useGlobalExperiencesSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalExperiencesSearch'
import ExperiencesTabContent from '../Components/Mobile/TabContents/ExperiencesTabContent'
import { pushWithRegion } from 'actions/NavigationActions'
import { type QueryStringSearchParams, replaceExperienceQueryString } from 'lib/experiences/experienceUtils'
import useQueryParams from 'hooks/useQueryParams'
import LineHotAirBalloonIcon from 'LuxKit/Icons/line/LineHotAirBalloonIcon'
import { useAppDispatch } from 'hooks/reduxHooks'
import { trackEvent } from 'analytics/analytics'
import { searchEventWithContext } from 'analytics/snowplow/events'
import { mapGlobalSearchContextToSnowplowSearchEvent } from 'analytics/mapSnowplowSearchTracking'
import GlobalSearchTrackingContext from 'contexts/GlobalSearch/GlobalSearchTracking'
import { OFFER_TYPE_EXPERIENCE } from 'constants/offer'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { GLOBAL_EXPERIENCES_SEARCH_INITIAL_STATE } from 'contexts/Experience/GlobalSearchState'
import useSearchVerticalLabels from 'hooks/GlobalSearch/useSearchVerticalLabels'
import { useRouteMatch } from 'react-router'

function useGlobalSearchVerticalTabExperiences(): App.GlobalSearchVerticalTabV2 {
  const dispatch = useAppDispatch()
  const queryParams = useQueryParams()

  const {
    globalExperiencesSearchDispatch: searchDispatch,
    globalExperiencesSearchState: state,
  } = useGlobalExperiencesSearchContext(GLOBAL_EXPERIENCES_SEARCH_INITIAL_STATE, queryParams)

  const onSearchEvent = useContext(GlobalSearchTrackingContext)

  const mapRouteMatch = useRouteMatch('/:regionCode/search/experiences/map')
  const isOnMapPage = !!mapRouteMatch

  const onSearch = useCallback(() => {
    const { searchItem, checkinDate, checkoutDate, dateSearchOptionId, experienceCategories } = state

    const toBeSearchedItem = searchItem ?? ANYWHERE_SEARCH_ITEM

    const formattedCheckinDate = checkinDate?.format(ISO_DATE_FORMAT)

    const effectiveCheckoutDate = checkoutDate ?? checkinDate
    const formattedCheckoutDate = effectiveCheckoutDate?.format(ISO_DATE_FORMAT)

    const query: QueryStringSearchParams = {
      checkIn: formattedCheckinDate,
      checkOut: formattedCheckoutDate,
    }

    if (experienceCategories?.length) {
      query.categories = experienceCategories.join(',')
    }

    if (toBeSearchedItem.searchType === 'experience') {
      query.experienceId = toBeSearchedItem.value
      query.experienceName = toBeSearchedItem.format.mainText
    } else {
      query.destinationId = toBeSearchedItem.value
      query.destinationName = toBeSearchedItem.format.mainText
    }

    const queryString = replaceExperienceQueryString(query)

    trackEvent(searchEventWithContext({
      searchEvent: { type: 'search_bar' },
      sortContext: { value: 'recommended' },
      searchContext: mapGlobalSearchContextToSnowplowSearchEvent({
        searchItem,
        checkinDate,
        checkoutDate: effectiveCheckoutDate,
        dateSearchOptionId,
        searchVerticals: new Set(['experiences']),
      },
      ),
    }))

    const path = isOnMapPage ? '/search/experiences/map' : '/search/experiences'

    dispatch(pushWithRegion(`${path}?${queryString}`))

    onSearchEvent('submit-button', {
      searchItem,
      vertical: OFFER_TYPE_EXPERIENCE,
      checkIn: formattedCheckinDate,
      checkOut: formattedCheckoutDate,
    })
  }, [state, dispatch, onSearchEvent, isOnMapPage])

  const verticalLabels = useSearchVerticalLabels()

  return useMemo((): App.GlobalSearchVerticalTabV2 => ({
    label: verticalLabels.experiences,
    key: 'experiences',
    context: { dispatch: searchDispatch, state },
    onSearch,
    ContentComponent: ExperiencesTabContent,
    Icon: LineHotAirBalloonIcon,
  }), [searchDispatch, state, onSearch, verticalLabels])
}

export default useGlobalSearchVerticalTabExperiences
